// The client half of `/masuk/staf` (DESIGN-VISUAL.md §21, docs/spec/CR-007.md) — the staff
// counterpart of `../SignInPanel.tsx`. Reads the `lanjut` query parameter the same way, so a staff
// visitor `middleware.ts` redirected from a protected console route lands back there after signing
// in (CR-007 §4.6, T-174's `signInPathFor` sends staff prefixes here). Split from `page.tsx` for the
// same reason as the investor panel: `useSearchParams` needs a `Suspense` boundary above it.
'use client';

import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback } from 'react';
// Relative, not `@/` — `frontend/vitest.config.ts` (T-03's, frozen) has no path-alias resolution.
import { NoticeBand } from '../../../../components/ui/NoticeBand';
import { SignInForm, useInvalidateSession, type AuthenticatedUser } from '../../../../features/auth';
// Read-only import — this task never writes `navigation.ts` (T-202's and T-19's file). It only
// reads `NAV_BY_ROLE` to find each staff role's own console home.
import { NAV_BY_ROLE, type Role } from '../../../../components/shell/navigation';

// `/akun` is the one page every role has — the fallback when there is no `lanjut` to return to
// *and* the signed-in role is not one of the three staff consoles below (a state the staff door
// should make impossible, but fail-closed rather than guess).
const DEFAULT_DESTINATION = '/akun';

// The three staff roles' own console home, taken from `NAV_BY_ROLE[role][0].href` — the same data
// `components/shell/navigation.ts` already uses to render each console's own navigation, so this
// adds no new routing table (T-201, CR-010).
const STAFF_CONSOLE_ROLES: readonly Role[] = ['ops', 'ppa_staff', 'superadmin'];

/** The signed-in role's own console home, or `/akun` for any other role (fail-closed: a role that
 * is not one of the three staff consoles never lands on a console route the backend would answer
 * `403` to). */
function consoleHomeFor(role: Role): string {
  if (!STAFF_CONSOLE_ROLES.includes(role)) {
    return DEFAULT_DESTINATION;
  }
  const home = NAV_BY_ROLE[role][0];
  return home ? home.href : DEFAULT_DESTINATION;
}

// A fixed, non-network placeholder origin, reused verbatim from `../SignInPanel.tsx` —
// `safeDestination`'s own comment there explains why both checks below are needed (T-19 r1 F-2,
// r2 F-11 payload families). A staff panel that re-implemented this loosely would be a new open
// redirect (CR-007, T-173 done_when (f)).
const PLACEHOLDER_ORIGIN = 'http://x.invalid';

/** Only a same-app, root-relative path is accepted; anything the URL parser resolves to a
 * different origin (an absolute URL, `//host/...`, `/\host/...`, `/\/host/...`, …) falls back to
 * `fallback` rather than sending the browser somewhere `middleware.ts` never intended `lanjut`
 * to point at. Same two checks as `../SignInPanel.tsx`'s `safeDestination` — see that file's
 * comment for their individual rationale — except the fallback is now the signed-in role's own
 * console home (`consoleHomeFor`) instead of always `/akun` (T-201, CR-010): `lanjut` still wins
 * whenever it is present and resolves to a same-app root-relative path. */
function safeDestination(raw: string | null, fallback: string): string {
  if (!raw) {
    return fallback;
  }
  let resolved: URL;
  try {
    resolved = new URL(raw, PLACEHOLDER_ORIGIN);
  } catch {
    return fallback;
  }
  if (resolved.origin !== PLACEHOLDER_ORIGIN || resolved.pathname.startsWith('//')) {
    return fallback;
  }
  return `${resolved.pathname}${resolved.search}${resolved.hash}`;
}

export function StaffSignInPanel() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const invalidateSession = useInvalidateSession();

  const handleSuccess = useCallback(
    (user: AuthenticatedUser) => {
      invalidateSession();
      router.push(safeDestination(searchParams.get('lanjut'), consoleHomeFor(user.role)));
    },
    [invalidateSession, router, searchParams],
  );

  return (
    <div className="flex w-full flex-col gap-s5">
      <h1 style={{ marginBottom: 0 }}>Masuk staf</h1>
      {searchParams.get('sesi') === 'berakhir' ? (
        <NoticeBand tone="info" title="Sesi berakhir">
          Sesi Anda sudah berakhir. Masuk kembali untuk melanjutkan.
        </NoticeBand>
      ) : null}
      <SignInForm onSuccess={handleSuccess} door="staff" />
      {/* No "Belum punya akun?" line (DESIGN-VISUAL.md §21 table): staff accounts are provisioned,
          never self-registered. */}
      <p className="auth-note">
        <Link href="/masuk" className="underline underline-offset-2">
          Masuk sebagai investor
        </Link>
        .
      </p>
    </div>
  );
}
